import React, { useState, useEffect, useCallback } from 'react';
import { FirmenSwitcher } from './FirmenSwitcher';
import { useAuthStore } from '../../store/authStore';
import { ModuleErrorBoundary } from '../ErrorBoundary';
import { loadSetting } from '../../services/settingsService';
import { GlobalSearch } from '../GlobalSearch';
import { CaptureModal } from '../CaptureModal';
import { useHandoffStore } from '../../store/handoffStore';
import { useFocusStore } from '../../store/focusStore';
import { openHandbuch } from '../../lib/openHandbuch';

interface BadgeCounts {
  vorsorge: number;
  wietz:    number;
  abrechnung: number;
}

function useBadgeCounts(): BadgeCounts {
  const [counts, setCounts] = useState<BadgeCounts>({ vorsorge: 0, wietz: 0, abrechnung: 0 });

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);

    type VguItem    = { status: string; faelligkeitDatum: string };
    type WietzItem  = { status: string; faelligkeitDatum: string };
    type RechnItem  = { status: string };

    void Promise.all([
      loadSetting<VguItem[]>('vgu_faelligkeiten_v1', []),
      loadSetting<WietzItem[]>('wietz_faelligkeiten_v1', []),
      loadSetting<RechnItem[]>('rechnungen_v1', []),
    ]).then(([vgu, wietz, rechn]) => {
      const vorsorge   = vgu.filter(f =>
        (f.status === 'UEBERFAELLIG') ||
        (f.status === 'OFFEN' && f.faelligkeitDatum < today)
      ).length;
      const wietzCount = wietz.filter(f =>
        (f.status === 'UEBERFAELLIG') ||
        (f.status === 'OFFEN' && f.faelligkeitDatum < today)
      ).length;
      const abrechnungCount = rechn.filter(r => r.status === 'gemahnt').length;
      setCounts({ vorsorge, wietz: wietzCount, abrechnung: abrechnungCount });
    });

    const interval = window.setInterval(() => {
      void Promise.all([
        loadSetting<VguItem[]>('vgu_faelligkeiten_v1', []),
        loadSetting<WietzItem[]>('wietz_faelligkeiten_v1', []),
        loadSetting<RechnItem[]>('rechnungen_v1', []),
      ]).then(([vgu, wietz, rechn]) => {
        const vorsorge   = vgu.filter(f =>
          (f.status === 'UEBERFAELLIG') ||
          (f.status === 'OFFEN' && f.faelligkeitDatum < today)
        ).length;
        const wietzCount = wietz.filter(f =>
          (f.status === 'UEBERFAELLIG') ||
          (f.status === 'OFFEN' && f.faelligkeitDatum < today)
        ).length;
        const abrechnungCount = rechn.filter(r => r.status === 'gemahnt').length;
        setCounts({ vorsorge, wietz: wietzCount, abrechnung: abrechnungCount });
      });
    }, 30_000);

    return () => { window.clearInterval(interval); };
  }, []);

  return counts;
}

export type ModuleKey =
  | 'dashboard'
  | 'assistent'
  | 'vorsorge'
  | 'wietz'
  | 'kalender'
  | 'akte'
  | 'protokolle'
  | 'dokumente'
  | 'begehung'
  | 'email'
  | 'formulare'
  | 'reporting'
  | 'asa'
  | 'zeit'
  | 'abrechnung'
  | 'einstellungen';

interface NavItem {
  key: ModuleKey;
  label: string;
  icon: string;
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  { label: 'Übersicht', items: [
    { key: 'dashboard',   label: 'Dashboard',       icon: '⊞' },
    { key: 'assistent',   label: 'KI-Assistent',    icon: '🤖' },
  ] },
  { label: 'Arbeitsmedizin', items: [
    { key: 'vorsorge',    label: 'Vorsorge',        icon: '🩺' },
    { key: 'begehung',    label: 'Begehung',        icon: '🔍' },
    { key: 'wietz',       label: 'WIETZ',           icon: '📋' },
    { key: 'asa',         label: 'ASA / GBU',       icon: '🛡' },
    { key: 'formulare',   label: 'Formulare',       icon: '📄' },
  ] },
  { label: 'Termine & Doku', items: [
    { key: 'kalender',    label: 'Kalender',        icon: '📅' },
    { key: 'akte',        label: 'Akte',            icon: '🗂' },
    { key: 'protokolle',  label: 'Protokolle',      icon: '📝' },
    { key: 'dokumente',   label: 'Dokumente',       icon: '📁' },
    { key: 'email',       label: 'E-Mail',          icon: '✉' },
  ] },
  { label: 'Finanzen', items: [
    { key: 'zeit',        label: 'Zeit & Leistung', icon: '⏱' },
    { key: 'abrechnung',  label: 'Abrechnung',      icon: '💶' },
    { key: 'reporting',   label: 'GF-Reporting',    icon: '📊' },
  ] },
  { label: 'System', items: [
    { key: 'einstellungen', label: 'Einstellungen', icon: '⚙' },
  ] },
];

const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap(g => g.items);

interface Props {
  children: (activeModule: ModuleKey, navigate: (m: ModuleKey) => void) => React.ReactNode;
}

function initialModule(): ModuleKey {
  const p = new URLSearchParams(window.location.search).get('module');
  const keys: ModuleKey[] = ['dashboard','assistent','vorsorge','wietz','kalender','akte','begehung','zeit','abrechnung','email','formulare','reporting','asa','einstellungen'];
  return (keys.includes(p as ModuleKey) ? p : 'dashboard') as ModuleKey;
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on  = (): void => { setOnline(true); };
    const off = (): void => { setOnline(false); };
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

export function AppLayout({ children }: Props): React.JSX.Element {
  const logout = useAuthStore(s => s.logout);
  const [active, setActive] = useState<ModuleKey>(initialModule);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const badges = useBadgeCounts();
  const [searchOpen, setSearchOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const handoffTarget = useHandoffStore(s => s.target);
  const focusModule = useFocusStore(s => s.module);
  const online = useOnlineStatus();
  const [navCollapsed, setNavCollapsed] = useState<Set<string>>(
    () => new Set<string>(JSON.parse(localStorage.getItem('amed-nav-collapsed') ?? '[]') as string[]),
  );
  const toggleNavGroup = useCallback((label: string): void => {
    setNavCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      localStorage.setItem('amed-nav-collapsed', JSON.stringify([...next]));
      return next;
    });
  }, []);

  const navigate = useCallback((m: ModuleKey): void => {
    setActive(m);
    setMobileOpen(false);
    window.history.replaceState({}, '', `/?module=${m}`);
  }, []);

  /* Modulübergreifende Folge-Aktion (z.B. Erfassung → Formular vorbefüllen):
   * zum Zielmodul wechseln; das Zielmodul holt die Payload beim Mount ab. */
  useEffect(() => {
    if (handoffTarget !== null) {
      navigate(handoffTarget);
      setCaptureOpen(false);
    }
  }, [handoffTarget, navigate]);

  /* Deep-Link aus der globalen Suche / Akte-Protokoll-Chip: zum Modul wechseln,
   * das Zielmodul öffnet den fokussierten Eintrag selbst. */
  useEffect(() => {
    if (focusModule !== null) { navigate(focusModule); setSearchOpen(false); }
  }, [focusModule, navigate]);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); };
  }, []);

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const activeItem = NAV_ITEMS.find(i => i.key === active);

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
      {/* Offline-Banner */}
      {!online && (
        <div data-no-print
          className="flex items-center justify-center gap-2 py-1.5 bg-orange-500 text-white text-xs font-medium z-40 flex-shrink-0">
          <span>📡</span>
          <span>Offline — Änderungen werden lokal gespeichert.</span>
        </div>
      )}

      {/* GlobalSearch Modal */}
      {searchOpen && (
        <GlobalSearch
          navigate={navigate}
          onClose={() => { setSearchOpen(false); }}
        />
      )}

      {/* ── Mobile Top-Bar (nur auf kleinen Screens) ── */}
      <div data-no-print className="md:hidden flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900
                  border-b border-gray-200 dark:border-gray-800 flex-shrink-0 z-20">
        <button
          onClick={() => { setMobileOpen(o => !o); }}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800
                     text-gray-600 dark:text-gray-300 transition-colors"
          aria-label="Navigation öffnen"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-md bg-blue-700 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">A</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm flex-shrink-0">AMed</span>
          {activeItem && (
            <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
              — {activeItem.icon} {activeItem.label}
            </span>
          )}
        </div>
        <button
          onClick={() => { setSearchOpen(true); }}
          className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800
                     text-gray-500 transition-colors flex-shrink-0"
          aria-label="Suchen"
        >
          🔍
        </button>
      </div>

      {/* ── Mobile Backdrop ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => { setMobileOpen(false); }}
        />
      )}

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* ── Sidebar ──
            Mobile: fixed drawer (off-screen by default, slides in when mobileOpen)
            Desktop: static in-flow sidebar with collapse */}
        <aside
          data-no-print
          className={[
            'flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800',
            'flex-shrink-0 transition-transform duration-200',
            // Mobile: fixed full-height drawer
            'fixed inset-y-0 left-0 z-40 w-72',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            // Desktop: in-flow, reset mobile overrides, use collapse width
            'md:static md:inset-y-auto md:z-auto md:translate-x-0 md:h-auto',
            collapsed ? 'md:w-14' : 'md:w-56',
          ].join(' ')}
        >
          {/* Logo + Collapse-Toggle (Desktop) / Close-Button (Mobile) */}
          <div className="flex items-center justify-between px-3 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-700 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              {(!collapsed || mobileOpen) && (
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">AMed</span>
              )}
            </div>
            {/* Mobile: X-Schließen */}
            <button
              onClick={() => { setMobileOpen(false); }}
              className="md:hidden p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
              aria-label="Navigation schließen"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Desktop: Collapse-Toggle */}
            <button
              onClick={() => { setCollapsed(c => !c); }}
              className="hidden md:block p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400
                         transition-colors ml-auto"
              title={collapsed ? 'Erweitern' : 'Einklappen'}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d={collapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7M19 19l-7-7 7-7'} />
              </svg>
            </button>
          </div>

          {/* Firmen-Switcher */}
          <div className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
            <FirmenSwitcher collapsed={collapsed && !mobileOpen} />
          </div>

          {/* Navigation (gruppiert) */}
          <nav className="flex-1 overflow-y-auto py-2 px-2">
            {NAV_GROUPS.map((group, gi) => {
              const showLabel = !collapsed || mobileOpen;
              const isCollapsed = showLabel && navCollapsed.has(group.label);
              return (
                <div key={group.label} className={gi > 0 ? 'mt-3' : ''}>
                  {showLabel ? (
                    <button onClick={() => { toggleNavGroup(group.label); }}
                      className="w-full flex items-center justify-between px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider
                                 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                      <span>{group.label}</span>
                      <svg className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  ) : (
                    gi > 0 && <div className="mx-2 mb-1 border-t border-gray-100 dark:border-gray-800" />
                  )}
                  {!isCollapsed && (
                  <div className="space-y-0.5">
                    {group.items.map(item => {
                      const liveBadge =
                        item.key === 'vorsorge'   ? badges.vorsorge   :
                        item.key === 'wietz'      ? badges.wietz      :
                        item.key === 'abrechnung' ? badges.abrechnung :
                        undefined;
                      const badge = liveBadge !== undefined && liveBadge > 0 ? liveBadge : item.badge;
                      return (
                        <button
                          key={item.key}
                          onClick={() => { navigate(item.key); }}
                          title={(!showLabel) ? item.label : undefined}
                          className={`relative flex items-center gap-3 w-full px-2 py-2.5 rounded-lg text-sm
                                     transition-colors text-left
                                     ${active === item.key
                                       ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 font-medium'
                                       : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                     }`}
                        >
                          <span className="text-base leading-none flex-shrink-0">{item.icon}</span>
                          {showLabel && (
                            <span className="flex-1 truncate">{item.label}</span>
                          )}
                          {showLabel && badge !== undefined && badge > 0 && (
                            <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400
                                             rounded-full px-1.5 py-0.5 font-medium">
                              {badge}
                            </span>
                          )}
                          {!showLabel && badge !== undefined && badge > 0 && (
                            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Suche + Dark-Mode + Logout */}
          <div className="px-2 py-3 border-t border-gray-100 dark:border-gray-800 space-y-0.5 flex-shrink-0">
            <button
              onClick={() => { setSearchOpen(true); setMobileOpen(false); }}
              title="Suchen (Strg+K)"
              className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-sm
                         text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-base flex-shrink-0">🔍</span>
              {(!collapsed || mobileOpen) && (
                <span className="flex-1 text-left">Suchen</span>
              )}
              {!collapsed && !mobileOpen && (
                <kbd className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-gray-400">⌃K</kbd>
              )}
            </button>
            <button
              onClick={() => { openHandbuch(); }}
              title="Benutzerhandbuch öffnen (PDF)"
              className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-sm
                         text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-base flex-shrink-0">📖</span>
              {(!collapsed || mobileOpen) && <span>Handbuch</span>}
            </button>
            <button
              onClick={() => { setIsDark(d => !d); }}
              title={isDark ? 'Hell-Modus' : 'Dunkel-Modus'}
              className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-sm
                         text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-base flex-shrink-0">{isDark ? '☀' : '🌙'}</span>
              {(!collapsed || mobileOpen) && <span>{isDark ? 'Hell' : 'Dunkel'}</span>}
            </button>
            <button
              onClick={logout}
              title={collapsed && !mobileOpen ? 'Sperren' : undefined}
              className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-sm
                         text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-base flex-shrink-0">🔒</span>
              {(!collapsed || mobileOpen) && <span>Sperren</span>}
            </button>
          </div>
        </aside>

        {/* ── Hauptinhalt ── */}
        <main className="flex-1 overflow-y-auto min-w-0">
          <ModuleErrorBoundary moduleName={active}>
            {children(active, navigate)}
          </ModuleErrorBoundary>
        </main>
      </div>

      {/* ── Mobile: schwebender Erfassungs-Button (Foto/Dokument/Beleg) ── */}
      <button data-no-print
        onClick={() => { setCaptureOpen(true); }}
        aria-label="Foto, Dokument oder Beleg erfassen"
        className="md:hidden fixed bottom-5 right-5 z-30 w-14 h-14 rounded-full bg-blue-700
                   text-white text-2xl shadow-lg shadow-blue-900/30 flex items-center
                   justify-center active:scale-95 transition-transform">
        📷
      </button>
      {captureOpen && (
        <CaptureModal
          onClose={() => { setCaptureOpen(false); }}
          onSaved={() => { window.dispatchEvent(new CustomEvent('amed:akte-changed')); }}
        />
      )}
    </div>
  );
}
